export type FormatNumberWithPrecisionOptions = {
  /**
   * Maximum number of decimal places to display (default: 4)
   */
  maxDecimals?: number;
  /**
   * Whether to include thousands separator (default: true)
   */
  useThousandsSeparator?: boolean;
  /**
   * Locale for number formatting (default: 'en-US')
   */
  locale?: string;
};

export type FormatNumberWithPrecisionResult = {
  /**
   * Formatted number string
   */
  formatted: string;
  /**
   * Whether the original number had more decimals than displayed
   */
  hasMoreDecimals: boolean;
  /**
   * Full unformatted value with all decimals
   */
  fullValue: string;
};

/**
 * Formats a number with smart decimal precision handling
 *
 * Rules:
 * - Uses max 4 decimals by default (configurable)
 * - Removes trailing zeros
 * - Rounds to max decimals if more exist
 * - Includes thousands separator
 * - Tracks if there are more decimals than displayed
 *
 * @example
 * formatNumberWithPrecision(2900.23)
 * // { formatted: "2,900.23", hasMoreDecimals: false, fullValue: "2900.23" }
 *
 * @example
 * formatNumberWithPrecision(2900.2324)
 * // { formatted: "2,900.2324", hasMoreDecimals: false, fullValue: "2900.2324" }
 *
 * @example
 * formatNumberWithPrecision(2900.235678)
 * // { formatted: "2,900.2357", hasMoreDecimals: true, fullValue: "2900.235678" }
 *
 * @example
 * formatNumberWithPrecision(2900.2340)
 * // { formatted: "2,900.234", hasMoreDecimals: false, fullValue: "2900.234" }
 */
export function formatNumberWithPrecision(
  value: number,
  options: FormatNumberWithPrecisionOptions = {},
): FormatNumberWithPrecisionResult {
  const {
    maxDecimals = 4,
    useThousandsSeparator = true,
    locale = 'en-US',
  } = options;

  // Convert to string to preserve full precision
  const fullValue = value.toString();

  // Check if value has more decimals than maxDecimals
  const decimalPart = fullValue.split('.')[1] || '';
  const hasMoreDecimals = decimalPart.length > maxDecimals;

  // Round to maxDecimals
  const roundedValue = Number(value.toFixed(maxDecimals));

  // Format with thousands separator if needed
  let formatted: string;
  if (useThousandsSeparator) {
    // Use toLocaleString to add thousands separator
    // Set maximumFractionDigits to preserve decimals
    formatted = roundedValue.toLocaleString(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxDecimals,
    });
  } else {
    // Just convert to string and remove trailing zeros
    formatted = roundedValue.toString();
  }

  return {
    formatted,
    hasMoreDecimals,
    fullValue,
  };
}

/**
 * Simple version that just returns the formatted string
 */
export function formatNumberWithPrecisionSimple(
  value: number,
  options: FormatNumberWithPrecisionOptions = {},
): string {
  return formatNumberWithPrecision(value, options).formatted;
}
